"use client";

import { CheckCircle2, Copy, KeyRound, Link2, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Card, TableFrame, TBody, Td, Th, THead, Tr } from "@/components/ui-system";
import { createAction } from "@/lib/api/endpoints/automation-actions";
import { createAlertRule } from "@/lib/api/endpoints/alerts";
import type { JsonObject } from "@/lib/api/types/json";
import { useAuthStore } from "@/modules/auth/store";
import { PrivateDeviceImage } from "@/modules/devices/components/private-device-image";
import { useCreateDeviceMutation } from "@/modules/devices/hooks";

import {
  connectivityProtocolLabel,
  formatFormulaExpression,
  formatSamplingRateHz,
  GRAPH_TYPE_OPTIONS,
  type ChannelRow,
  type DataloggerAlertCondition,
  type DataloggerFormSnapshot,
  type DataloggerProvisioningResult,
  validateStep,
} from "../../schema/datalogger.schema";
import { useDataloggerWizardStore } from "../../hooks/use-datalogger-form";
import { useWarrantyPreview } from "../../hooks/use-warranty-preview";
import { buildDeviceCreatePayload, resolveMqttTelemetryTopic } from "../../utils/build-create-payload";
import { generateApiKey, generateSerialNumber, generateUUID } from "../../utils/generators";

function splitCommaList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function humanizeProtocolKey(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function humanizeAlertSource(value: DataloggerAlertCondition["source"]): string {
  switch (value) {
    case "warning_low":
      return "Warning low";
    case "warning_high":
      return "Warning high";
    default:
      return "Manual";
  }
}

function findChannelLabel(channels: ChannelRow[], channelKey: string): string {
  return channels.find((channel) => channel.channelKey === channelKey)?.name || channelKey;
}

function graphTypeLabel(value: ChannelRow["graphType"]): string {
  return GRAPH_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value.replace(/_/g, " ");
}

function buildAlertRuleName(alert: DataloggerAlertCondition, channels: ChannelRow[]): string {
  const channelLabel = findChannelLabel(channels, alert.channelKey).trim() || alert.channelKey.trim() || "Channel";
  if (alert.source === "warning_low") {
    return `${channelLabel} warning low`;
  }
  if (alert.source === "warning_high") {
    return `${channelLabel} warning high`;
  }
  return `${channelLabel} threshold`;
}

function buildRecipientSummary(snapshot: DataloggerFormSnapshot) {
  const audience = [
    { label: "Admin", prefs: snapshot.alertRecipients.admin },
    { label: "Distributor", prefs: snapshot.alertRecipients.distributor },
    { label: "Users", prefs: snapshot.alertRecipients.users },
  ]
    .map(({ label, prefs }) => {
      const methods = [
        prefs.email ? "email" : null,
        prefs.message ? "message" : null,
        prefs.inApp ? "in app" : null,
      ].filter(Boolean);
      const targeting = prefs.selectAll ? `all ${label.toLowerCase()}` : prefs.userIds.length > 0 ? `${prefs.userIds.length} selected` : "all matching";
      return methods.length > 0 ? `${label}: ${methods.join(", ")} (${targeting})` : null;
    })
    .filter(Boolean) as string[];

  const channels: string[] = [];
  const emails = splitCommaList(snapshot.alertRecipients.emailRecipients);
  const numbers = splitCommaList(snapshot.alertRecipients.messageRecipients);
  if (emails.length > 0) {
    channels.push(`Additional email: ${emails.join(", ")}`);
  }
  if (numbers.length > 0) {
    channels.push(`Additional message: ${numbers.join(", ")}`);
  }

  return { audience, channels };
}

function hasAudienceConfig(snapshot: DataloggerFormSnapshot): boolean {
  return [snapshot.alertRecipients.admin, snapshot.alertRecipients.distributor, snapshot.alertRecipients.users].some(
    (prefs) => prefs.email || prefs.message || prefs.inApp,
  );
}

function buildEmailActionConfig(snapshot: DataloggerFormSnapshot): JsonObject {
  return {
    audience: snapshot.alertRecipients,
    email_recipients: splitCommaList(snapshot.alertRecipients.emailRecipients),
    message_recipients: splitCommaList(snapshot.alertRecipients.messageRecipients),
    subject: snapshot.basic.name.trim() ? `Alert: ${snapshot.basic.name.trim()}` : "Alert triggered",
  };
}

export function StepReview() {
  const dialogs = useAppDialogs();
  const getSnapshot = useDataloggerWizardStore((s) => s.getSnapshot);
  const reset = useDataloggerWizardStore((s) => s.reset);
  const provisioningResult = useDataloggerWizardStore((s) => s.provisioningResult);
  const setProvisioningResult = useDataloggerWizardStore((s) => s.setProvisioningResult);

  const { basic, channels } = useDataloggerWizardStore(
    useShallow((s) => ({
      basic: s.basic,
      channels: s.channels,
    })),
  );

  const snapshot = getSnapshot();
  const reviewErrors = validateStep(7, snapshot);
  const protocolEntries = Object.entries(snapshot.hardware.protocolSettings ?? {}).filter(([key, value]) => {
    if (key === "mqttPassword" || key === "wifiPassword" || key === "loraAppKey") {
      return false;
    }
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return true;
  });
  const recipientSummary = buildRecipientSummary(snapshot);
  const warrantyPreview = useWarrantyPreview(snapshot.lifecycle.warrantyEnd);
  const warrantyEndLabel =
    warrantyPreview.warrantyEnd || (warrantyPreview.isLoading ? "Loading workspace policy..." : "Set by workspace policy");

  const tenantId = useAuthStore((s) => s.tenantId);
  const createMut = useCreateDeviceMutation();
  const [alertError, setAlertError] = useState<string | null>(null);

  const copyText = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      await dialogs.copy({
        title: `Copy ${label}`,
        description: "Clipboard access is unavailable. Copy the value manually below.",
        label,
        value,
      });
    }
  };

  async function onSubmit() {
    if (reviewErrors.length > 0) return;
    setAlertError(null);
    const credentials = {
      deviceUid: generateUUID(),
      serialNumber: generateSerialNumber(),
      apiKey: generateApiKey(),
    };
    try {
      const payload = buildDeviceCreatePayload(getSnapshot(), null, credentials);
      const device = await createMut.mutateAsync(payload);
      let alertRulesCreated = 0;
      let alertTemplateError: string | null = null;

      const enabledAlerts = snapshot.alerts.filter((alert) => alert.enabled);
      if (enabledAlerts.length > 0 && tenantId) {
        try {
          const actionIds: string[] = [];

          if (
            hasAudienceConfig(snapshot) ||
            splitCommaList(snapshot.alertRecipients.emailRecipients).length > 0 ||
            splitCommaList(snapshot.alertRecipients.messageRecipients).length > 0
          ) {
            const action = await createAction({
              tenant_id: tenantId,
              type: "email",
              config: buildEmailActionConfig(snapshot),
            });
            actionIds.push(action.id);
          }

          for (const alert of enabledAlerts) {
            await createAlertRule({
              name: buildAlertRuleName(alert, snapshot.channels),
              tenant_id: tenantId,
              data_source: {
                device_id: device.id,
                channel: alert.channelKey.trim(),
              },
              operator: alert.operator,
              threshold: Number(alert.threshold),
              cooldown_minutes: 5,
              evaluation_window: "last_1h",
              aggregation: "avg",
              evaluation_mode: "latest_value",
              severity: "warning",
              action_ids: actionIds,
              is_active: true,
            });
            alertRulesCreated += 1;
          }
        } catch (err) {
          const baseMessage = err instanceof Error ? err.message : "Device created, but alert rule creation failed.";
          alertTemplateError =
            alertRulesCreated > 0
              ? `Created ${alertRulesCreated} of ${enabledAlerts.length} alert rules. ${baseMessage}`
              : baseMessage;
          setAlertError(alertTemplateError);
        }
      } else if (enabledAlerts.length > 0) {
        alertTemplateError = "No alert rules were created because this data logger is not assigned to a workspace yet.";
        setAlertError(alertTemplateError);
      }

      const nextResult: DataloggerProvisioningResult = {
        id: device.id,
        device_uid: device.device_uid,
        serialNumber: credentials.serialNumber,
        apiKey: credentials.apiKey,
        mqttTopic:
          snapshot.hardware.transmissionMode === "mqtt"
            ? resolveMqttTelemetryTopic(snapshot, credentials.deviceUid)
            : null,
        mqttPublisherClientId:
          snapshot.hardware.transmissionMode === "mqtt"
            ? snapshot.hardware.protocolSettings.mqttPublisherClientId?.trim() || credentials.deviceUid
            : null,
        alertRulesCreated,
        alertRulesRequested: enabledAlerts.length,
        alertTemplateError,
      };
      setProvisioningResult(nextResult);
    } catch {
      /* mutation error surfaced below */
    }
  }

  if (provisioningResult) {
    return (
      <div className="relative overflow-hidden rounded-[var(--radius-xl)] border border-success/25 bg-card shadow-card">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 15% 0%, var(--semantic-success-surface), transparent 55%), radial-gradient(ellipse 40% 35% at 95% 10%, var(--mesh-blue), transparent 50%)",
          }}
        />
        <div className="h-px bg-gradient-to-r from-transparent via-success/50 to-transparent" />

        <div className="relative space-y-ds-7 p-ds-6 md:p-ds-8">
          <div className="flex flex-wrap items-start gap-ds-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-success/30 bg-success/15 text-success shadow-card">
              <CheckCircle2 className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 pt-ds-1">
              <p className="text-[0.625rem] font-bold uppercase tracking-[0.18em] text-success/80">
                Provisioned
              </p>
              <h3 className="mt-ds-1 text-title font-semibold text-foreground">Secure these credentials</h3>
              <p className="mt-ds-2 max-w-xl text-caption leading-relaxed text-muted/70">
                The API key is shown once in this session. Persist it in your vault. A copy is also
                embedded in device metadata for this development environment.
              </p>
            </div>
          </div>

          <dl className="grid gap-ds-3 md:grid-cols-2 xl:grid-cols-3">
            <div className="rounded-[var(--radius-lg)] border border-border/40 bg-muted-bg/30 p-ds-4 backdrop-blur-sm">
              <dt className="mb-ds-2 flex items-center gap-ds-2 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted/60">
                <KeyRound className="h-3 w-3 text-primary" aria-hidden />
                Serial
              </dt>
              <dd className="break-all text-body text-foreground">{provisioningResult.serialNumber}</dd>
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-ds-3 gap-ds-1"
                onClick={() => void copyText("serial", provisioningResult.serialNumber)}
              >
                <Copy className="h-3 w-3" aria-hidden />
                Copy
              </button>
            </div>

            <div className="rounded-[var(--radius-lg)] border border-border/40 bg-muted-bg/30 p-ds-4 backdrop-blur-sm">
              <dt className="mb-ds-2 flex items-center gap-ds-2 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted/60">
                <Link2 className="h-3 w-3 text-primary" aria-hidden />
                Device ID
              </dt>
              <dd className="break-all text-body text-foreground">{provisioningResult.id}</dd>
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-ds-3 gap-ds-1"
                onClick={() => void copyText("device_id", provisioningResult.id)}
              >
                <Copy className="h-3 w-3" aria-hidden />
                Copy
              </button>
            </div>

            <div className="rounded-[var(--radius-lg)] border border-border/40 bg-muted-bg/30 p-ds-4 backdrop-blur-sm">
              <dt className="mb-ds-2 flex items-center gap-ds-2 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted/60">
                <Link2 className="h-3 w-3 text-primary" aria-hidden />
                Device UID
              </dt>
              <dd className="break-all text-body text-foreground">{provisioningResult.device_uid}</dd>
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-ds-3 gap-ds-1"
                onClick={() => void copyText("device_uid", provisioningResult.device_uid)}
              >
                <Copy className="h-3 w-3" aria-hidden />
                Copy
              </button>
            </div>

            <div className="rounded-[var(--radius-lg)] border border-primary/15 bg-primary/10 p-ds-4 backdrop-blur-sm md:col-span-2 xl:col-span-3">
              <dt className="mb-ds-2 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted/60">
                API key
              </dt>
              <dd className="break-all text-[0.8rem] leading-relaxed text-foreground">{provisioningResult.apiKey}</dd>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-ds-3 gap-ds-1"
                onClick={() => void copyText("api_key", provisioningResult.apiKey)}
              >
                <Copy className="h-3 w-3" aria-hidden />
                Copy key
              </button>
            </div>

            {provisioningResult.mqttTopic ? (
              <div className="rounded-[var(--radius-lg)] border border-border/40 bg-muted-bg/30 p-ds-4 backdrop-blur-sm md:col-span-2 xl:col-span-3">
                <dt className="mb-ds-2 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted/60">
                  MQTT topic
                </dt>
                <dd className="break-all text-[0.8rem] leading-relaxed text-foreground">{provisioningResult.mqttTopic}</dd>
                <div className="mt-ds-3 flex flex-wrap gap-ds-2">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm gap-ds-1"
                    onClick={() => void copyText("mqtt_topic", provisioningResult.mqttTopic ?? "")}
                  >
                    <Copy className="h-3 w-3" aria-hidden />
                    Copy topic
                  </button>
                  {provisioningResult.mqttPublisherClientId ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm gap-ds-1"
                      onClick={() => void copyText("mqtt_publisher_client_id", provisioningResult.mqttPublisherClientId ?? "")}
                    >
                      <Copy className="h-3 w-3" aria-hidden />
                      Copy client ID
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </dl>

          {provisioningResult.alertTemplateError ? (
            <p
              className="rounded-[var(--radius-lg)] border border-warning/25 bg-warning/10 px-ds-4 py-ds-3 text-caption text-warning"
              role="alert"
            >
              Device was created, but alert rule provisioning was incomplete: {provisioningResult.alertTemplateError}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-ds-2 border-t border-border/25 pt-ds-5">
            <Link to={`/devices/${provisioningResult.id}`} className="btn btn-primary !no-underline">
              Open console
            </Link>
            {provisioningResult.alertRulesCreated > 0 ? (
              <Link to={`/devices/${provisioningResult.id}?tab=alerts`} className="btn btn-secondary !no-underline">
                View alerts
              </Link>
            ) : null}
            <Link to="/devices" className="btn btn-secondary !no-underline">
              Fleet grid
            </Link>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setProvisioningResult(null);
                setAlertError(null);
                reset();
              }}
            >
              Create another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-ds-6">
      {reviewErrors.length > 0 ? (
        <ul className="list-inside list-disc rounded-[var(--radius-lg)] border border-error/30 bg-error/10 px-ds-4 py-ds-3 text-caption text-error">
          {reviewErrors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}

      <div className="grid gap-ds-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.75fr)_minmax(0,0.85fr)]">
        <Card className="border-border/35 bg-gradient-to-b from-card/95 to-card/70 p-ds-5 shadow-card">
          <h3 className="mb-ds-4 text-[0.625rem] font-bold uppercase tracking-[0.15em] text-muted/60">
            Identity bundle
          </h3>
          <div className="grid gap-ds-3 lg:grid-cols-[minmax(0,1fr)_18rem]">
            <dl className="grid gap-ds-2 text-caption text-foreground sm:grid-cols-2">
              {[
                { label: "Name", value: basic.name },
                {
                  label: "Sim type",
                  value:
                    basic.simType === "industrial"
                      ? "Industrial"
                      : basic.simType === "other"
                        ? "Other"
                        : "Phone",
                },
                ...(basic.phoneNumber.trim()
                  ? [{ label: "Phone number", value: basic.phoneNumber.trim(), mono: true as const }]
                  : []),
                ...(basic.imeiNumber.trim()
                  ? [{ label: "IMEI number", value: basic.imeiNumber.trim(), mono: true as const }]
                  : []),
                { label: "Protocol / uplink", value: connectivityProtocolLabel(snapshot.hardware.transmissionMode) },
                { label: "Location", value: basic.location },
                { label: "City", value: basic.city },
                { label: "State", value: basic.state },
                ...(basic.latitude.trim() && basic.longitude.trim()
                  ? [
                      {
                        label: "Coordinates (WGS84)",
                        value: `${basic.latitude.trim()} deg, ${basic.longitude.trim()} deg`,
                        mono: true as const,
                      },
                    ]
                  : []),
                { label: "Description", value: basic.description },
                { label: "Installation date", value: snapshot.lifecycle.installationDate, mono: true },
                { label: "Warranty end", value: warrantyEndLabel, mono: true },
                { label: "Next calibration", value: snapshot.lifecycle.nextCalibration, mono: true },
                {
                  label: "Tags",
                  value: basic.tags.length ? basic.tags.join(" - ") : null,
                  mono: true,
                },
              ].map(({ label, value, mono }) => (
                <div
                  key={label}
                  className="flex flex-col gap-0.5 rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2"
                >
                  <dt className="text-[0.6rem] uppercase tracking-wider text-muted/50">{label}</dt>
                  <dd className={mono ? "text-micro text-foreground/80" : "font-medium"}>
                    {value || <span className="text-muted/35">-</span>}
                  </dd>
                </div>
              ))}
            </dl>

            {basic.deviceImagePreviewUrl || basic.deviceImagePath ? (
              <div className="rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-3 lg:row-span-4">
                <p className="text-[0.6rem] uppercase tracking-wider text-muted/50">Device photo</p>
                <div className="mt-ds-3 flex h-full min-h-[16rem] items-center justify-center rounded-md border border-border/40 bg-card/60 p-ds-2">
                  {basic.deviceImagePreviewUrl ? (
                    <img
                      src={basic.deviceImagePreviewUrl}
                      alt={basic.name ? `${basic.name} preview` : "Device preview"}
                      className="max-h-[20rem] w-full rounded-md object-contain"
                    />
                  ) : basic.deviceImagePath ? (
                    <PrivateDeviceImage
                      photoPath={basic.deviceImagePath}
                      alt={basic.name ? `${basic.name} preview` : "Device preview"}
                      className="max-h-[20rem] w-full rounded-md object-contain"
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        <Card className="border-border/35 bg-gradient-to-b from-card/95 to-card/70 p-ds-5 shadow-card">
          <h3 className="mb-ds-4 text-[0.625rem] font-bold uppercase tracking-[0.15em] text-muted/60">
            Signal defaults
          </h3>
          <dl className="grid gap-ds-2 text-caption text-foreground">
            {[
              { label: "Channels", value: String(channels.length), mono: true },
              {
                label: "Alert conditions",
                value: String(snapshot.alerts.filter((alert) => alert.enabled).length),
                mono: true,
              },
              {
                label: "Sampling (baseline)",
                value: formatSamplingRateHz(snapshot.hardware.samplingRate),
                accent: true,
                mono: true,
              },
              {
                label: "Transmission mode",
                value: connectivityProtocolLabel(snapshot.hardware.transmissionMode),
              },
            ].map(({ label, value, accent, mono }) => (
              <div
                key={label}
                className="flex flex-col gap-0.5 rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2"
              >
                <dt className="text-[0.6rem] uppercase tracking-wider text-muted/50">{label}</dt>
                <dd
                  className={
                    mono
                      ? `font-medium tabular-nums ${accent ? "text-primary" : "text-foreground/90"}`
                      : "font-medium"
                  }
                >
                  {value || <span className="text-muted/35">-</span>}
                </dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card className="border-border/35 bg-gradient-to-b from-card/95 to-card/70 p-ds-5 shadow-card">
          <h3 className="mb-ds-4 text-[0.625rem] font-bold uppercase tracking-[0.15em] text-muted/60">
            Connectivity profile
          </h3>
          <div className="grid gap-ds-2 sm:grid-cols-2 xl:grid-cols-1">
            {protocolEntries.length > 0 ? (
              protocolEntries.map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2"
                >
                  <p className="text-[0.6rem] uppercase tracking-wider text-muted/50">{humanizeProtocolKey(key)}</p>
                  <p className="mt-0.5 break-all text-micro text-foreground/85">
                    {typeof value === "boolean" ? (value ? "Enabled" : "Disabled") : String(value)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-caption text-muted">No protocol-specific settings entered.</p>
            )}
          </div>
        </Card>
      </div>

      <div>
        <h3 className="mb-ds-3 text-[0.625rem] font-bold uppercase tracking-[0.15em] text-muted/60">
          Alert conditions
        </h3>
        <div className="grid gap-ds-3 xl:grid-cols-2">
          {snapshot.alerts.filter((alert) => alert.enabled).length > 0 ? (
            snapshot.alerts
              .filter((alert) => alert.enabled)
              .map((alert, index) => {
                const recipients = [
                  ...(recipientSummary.audience.length > 0 ? [`Audience: ${recipientSummary.audience.join(", ")}`] : []),
                  ...recipientSummary.channels,
                ];

                return (
                  <Card key={`${alert.channelKey}-${alert.source}-${index}`} className="border-border/35 bg-card/80 p-ds-4 shadow-sm">
                    <div className="flex items-start justify-between gap-ds-3">
                      <div>
                        <p className="text-[0.625rem] uppercase tracking-[0.14em] text-muted/50">Alert {index + 1}</p>
                        <h4 className="mt-1 text-body font-semibold text-foreground">{buildAlertRuleName(alert, channels)}</h4>
                      </div>
                      <span className="rounded-full border border-border/50 px-2 py-1 text-micro text-foreground/70">
                        {humanizeAlertSource(alert.source)}
                      </span>
                    </div>
                    <dl className="mt-ds-3 grid gap-ds-2 sm:grid-cols-2 xl:grid-cols-3">
                      <div className="rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2">
                        <dt className="text-[0.6rem] uppercase tracking-wider text-muted/50">Condition</dt>
                        <dd className="mt-0.5 text-micro text-foreground/85">
                          {findChannelLabel(channels, alert.channelKey)} {alert.operator} {alert.threshold}
                        </dd>
                      </div>
                      <div className="rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2">
                        <dt className="text-[0.6rem] uppercase tracking-wider text-muted/50">Defaults</dt>
                        <dd className="mt-0.5 text-micro text-foreground/85">
                          Last 1 hour / Average / Window average
                        </dd>
                      </div>
                      <div className="rounded-lg border border-border/30 bg-muted-bg/20 px-ds-3 py-ds-2">
                        <dt className="text-[0.6rem] uppercase tracking-wider text-muted/50">Recipients</dt>
                        <dd className="mt-0.5 text-micro text-foreground/85">
                          {recipients.length > 0 ? recipients.join(" - ") : "None"}
                        </dd>
                      </div>
                    </dl>
                  </Card>
                );
              })
          ) : (
            <Card className="border-border/35 bg-card/80 p-ds-4 shadow-sm">
              <p className="text-caption text-muted">No alert rules will be created for this logger.</p>
            </Card>
          )}
        </div>
      </div>

      <div>
        <h3 className="mb-ds-3 text-[0.625rem] font-bold uppercase tracking-[0.15em] text-muted/60">
          Channel manifest
        </h3>
        <div className="overflow-hidden rounded-[var(--radius-lg)] border border-border/35">
          <div className="overflow-x-auto">
            <TableFrame className="min-w-[40rem] border-0 shadow-none">
              <THead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Key</Th>
                  <Th>Type</Th>
                  <Th>Graph</Th>
                  <Th>Aggregations</Th>
                  <Th>Formula</Th>
                </Tr>
              </THead>
              <TBody>
                {channels.map((c) => (
                  <Tr key={c.id}>
                    <Td className="font-medium">{c.name}</Td>
                    <Td className="text-caption">{c.channelKey}</Td>
                    <Td className="text-caption">{c.dataType}</Td>
                    <Td className="text-caption">{graphTypeLabel(c.graphType)}</Td>
                    <Td>
                      {c.aggregations.length ? (
                        <span className="text-caption">{c.aggregations.join(", ")}</span>
                      ) : (
                        <span className="text-muted/35">-</span>
                      )}
                    </Td>
                    <Td className="text-micro max-w-[12rem] truncate">
                      {c.formula ? formatFormulaExpression(c.formula) : "-"}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </TableFrame>
          </div>
        </div>
      </div>

      {createMut.isError ? (
        <p
          className="rounded-[var(--radius-lg)] border border-error/25 bg-error/10 px-ds-4 py-ds-3 text-caption text-error"
          role="alert"
        >
          {createMut.error instanceof Error ? createMut.error.message : "Create request failed."}
        </p>
      ) : null}

      {alertError ? (
        <p
          className="rounded-[var(--radius-lg)] border border-warning/25 bg-warning/10 px-ds-4 py-ds-3 text-caption text-warning"
          role="alert"
        >
          {alertError}
        </p>
      ) : null}

      <div className="flex flex-col gap-ds-4 rounded-[var(--radius-lg)] border border-border/35 bg-muted-bg/15 p-ds-5 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xl text-caption text-muted/70">
          Creates an unassigned data logger, its channels, and one-time provisioning credentials. Assign it to a workspace later from device management.
        </p>
        <button
          type="button"
          className="btn btn-primary shrink-0 gap-ds-2"
          disabled={createMut.isPending || reviewErrors.length > 0}
          onClick={() => void onSubmit()}
        >
          <Rocket className="h-4 w-4" aria-hidden />
          {createMut.isPending ? "Minting node..." : "Create and mint credentials"}
        </button>
      </div>
    </div>
  );
}
